/**
 * #699 — cold-page send gate.
 *
 * A WhatsApp Web tab that was asleep (UNLAUNCHED) comes back reporting `connected` before its store
 * has synced. Every message that was due meanwhile then dispatches at once: each store-check
 * (#462) times out at 120s and the page IPC wedges for 190s, so the whole batch is stranded
 * (18 recurring texts, 12 never confirmed). Two bounded, fail-open guards:
 *
 *  1. `waitForPageReady` — don't start the store-check / send while the engine says
 *     `hasSynced === false`. Unknown (undefined/null) counts as ready, so engines that don't report it
 *     are unaffected.
 *  2. `ColdPageSendPacer` — until one send has succeeded on this page, let ONE send at a time
 *     through (leased, so a wedged holder frees the slot after `leaseMs`), and let a waiter give up
 *     after `maxWaitMs`. A give-up returns before the can-i-send lock is taken, so the scheduler
 *     simply re-offers the message — same as the "not connected" early return.
 */

export interface PageStatus {
    connected: boolean;
    hasSynced?: boolean | null;
    state?: string | null;
    stream?: string | null;
}

export const PAGE_READY_TIMEOUT_MS = 60_000;
export const PAGE_READY_POLL_MS = 2_000;

export const isPageReady = (s: PageStatus): boolean => s.connected && s.hasSynced !== false;

export interface PageReadyResult {
    ready: boolean;
    waitedMs: number;
    last: PageStatus;
}

export async function waitForPageReady(
    poll: () => Promise<PageStatus>,
    first: PageStatus,
    opts: {
        timeoutMs?: number;
        pollMs?: number;
        now?: () => number;
        sleep?: (ms: number) => Promise<void>;
    } = {},
): Promise<PageReadyResult> {
    const timeoutMs = opts.timeoutMs ?? PAGE_READY_TIMEOUT_MS;
    const pollMs = opts.pollMs ?? PAGE_READY_POLL_MS;
    const now = opts.now ?? Date.now;
    const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    const startedAt = now();
    let last = first;
    while (!isPageReady(last)) {
        if (now() - startedAt >= timeoutMs) return { ready: false, waitedMs: now() - startedAt, last };
        await sleep(pollMs);
        try {
            last = await poll();
        } catch {
            // A status probe that throws is not evidence the page is unready: keep waiting until the deadline.
        }
    }
    return { ready: true, waitedMs: now() - startedAt, last };
}

export interface ColdPageSlot {
    release: () => void;
    waitedMs: number;
}

interface Waiter {
    startedAt: number;
    grant: (slot: ColdPageSlot) => void;
    giveUp: () => void;
}

export class ColdPageSendPacer {
    private warm = false;
    private active = 0;
    private readonly queue: Waiter[] = [];

    constructor(private readonly cfg: {
        coldConcurrency?: number;
        leaseMs?: number;
        maxWaitMs?: number;
        now?: () => number;
    } = {}) {}

    private get coldConcurrency(): number { return this.cfg.coldConcurrency ?? 1; }
    private get leaseMs(): number { return this.cfg.leaseMs ?? 45_000; }
    private get maxWaitMs(): number { return this.cfg.maxWaitMs ?? 90_000; }
    private now(): number { return (this.cfg.now ?? Date.now)(); }

    isWarm(): boolean { return this.warm; }

    /** A send succeeded on this page: the burst can no longer wedge on a cold store. */
    markWarm(): void {
        this.warm = true;
        while (this.queue.length > 0) this.pump();
    }

    /** The page went away / came back unsynced: pace again. */
    markCold(): void { this.warm = false; }

    /** Resolves with a slot, or null when the wait exceeded `maxWaitMs` (caller defers the message). */
    acquire(): Promise<ColdPageSlot | null> {
        if (this.warm) return Promise.resolve({ release: () => undefined, waitedMs: 0 });
        const startedAt = this.now();
        return new Promise<ColdPageSlot | null>((resolve) => {
            let settled = false;
            const waiter: Waiter = {
                startedAt,
                grant: (slot) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(waitTimer);
                    resolve(slot);
                },
                giveUp: () => {
                    if (settled) return;
                    settled = true;
                    const i = this.queue.indexOf(waiter);
                    if (i >= 0) this.queue.splice(i, 1);
                    resolve(null);
                },
            };
            const waitTimer = setTimeout(waiter.giveUp, this.maxWaitMs);
            this.queue.push(waiter);
            this.pump();
        });
    }

    private pump(): void {
        while (this.queue.length > 0 && (this.warm || this.active < this.coldConcurrency)) {
            const waiter = this.queue.shift()!;
            if (this.warm) {
                waiter.grant({ release: () => undefined, waitedMs: this.now() - waiter.startedAt });
                continue;
            }
            this.active += 1;
            let released = false;
            const release = (): void => {
                if (released) return;
                released = true;
                clearTimeout(lease);
                this.active -= 1;
                this.pump();
            };
            const lease = setTimeout(release, this.leaseMs);
            waiter.grant({ release, waitedMs: this.now() - waiter.startedAt });
        }
    }
}

/** One pacer per content-script lifetime == per page lifetime: a reloaded page starts cold. */
export const coldPageSendPacer = new ColdPageSendPacer();
